import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShareNetwork, CaretLeft, FacebookLogo, TiktokLogo, ArrowsClockwise, GearSix, CalendarBlank } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const PROVIDERS = {
  meta: { label: "Meta — Facebook e Instagram", icon: FacebookLogo, color: "text-sky-400", accent: "sky" },
  tiktok: { label: "TikTok Ads", icon: TiktokLogo, color: "text-fuchsia-400", accent: "fuchsia" },
};

const STATUS_COLOR = (s) => {
  const v = (s || "").toUpperCase();
  if (v.includes("ACTIVE") || v.includes("ENABLE")) return "text-emerald-400";
  if (v.includes("PAUSE") || v.includes("DISABLE")) return "text-amber-300";
  return "text-white/40";
};

const CampaignsTable = ({ provider, campaigns }) => (
  <div className="overflow-x-auto mt-4">
    <table className="w-full text-xs" data-testid={`social-${provider}-campaigns-table`}>
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-white/30 text-left">
          <th className="py-2 pr-2 font-medium">Campagna</th>
          <th className="py-2 pr-2 font-medium">Stato</th>
          <th className="py-2 pr-2 font-medium text-right">Budget/g</th>
          <th className="py-2 pr-2 font-medium text-right">Spesa 30gg</th>
          {provider === "meta" && <th className="py-2 pr-2 font-medium text-right">Copertura</th>}
          <th className="py-2 pr-2 font-medium text-right">Impressioni</th>
          <th className="py-2 pr-2 font-medium text-right">Clic</th>
          <th className="py-2 font-medium text-right">Conv.</th>
        </tr>
      </thead>
      <tbody>
        {campaigns.map((c) => (
          <tr key={c.id} className="border-t border-white/5 text-white/75">
            <td className="py-2.5 pr-2 font-semibold truncate max-w-[180px]" title={c.name}>{c.name}</td>
            <td className={`py-2.5 pr-2 text-[10px] font-semibold uppercase tracking-wider ${STATUS_COLOR(c.status)}`}>{c.status || "—"}</td>
            <td className="py-2.5 pr-2 text-right">{c.daily_budget != null ? `€ ${Number(c.daily_budget).toFixed(2)}` : "—"}</td>
            <td className="py-2.5 pr-2 text-right font-semibold">€ {Number(c.spend || 0).toFixed(2)}</td>
            {provider === "meta" && <td className="py-2.5 pr-2 text-right">{c.reach ?? "—"}</td>}
            <td className="py-2.5 pr-2 text-right">{c.impressions ?? "—"}</td>
            <td className="py-2.5 pr-2 text-right">{c.clicks ?? "—"}</td>
            <td className="py-2.5 text-right">{c.conversions || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ProviderCard = ({ provider, cfg, reload }) => {
  const meta = PROVIDERS[provider];
  const Icon = meta.icon;
  const [accounts, setAccounts] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(null);

  const loadAccounts = () => {
    api.get(`/admin/social/${provider}/accounts`)
      .then(({ data }) => setAccounts(provider === "meta" ? data.ad_accounts : data.advertisers))
      .catch((err) => { setAccounts([]); setError(formatApiErrorDetail(err.response?.data?.detail)); });
  };

  const loadCampaigns = () => {
    setLoading(true);
    setError(null);
    api.get(`/admin/social/${provider}/campaigns`)
      .then(({ data }) => setCampaigns(data.campaigns))
      .catch((err) => { setCampaigns(null); setError(formatApiErrorDetail(err.response?.data?.detail)); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (cfg?.connected) {
      loadAccounts();
      if (cfg.active_account) loadCampaigns();
    }
  }, [cfg?.connected, cfg?.active_account]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAccount = async (a) => {
    const id = provider === "meta" ? (a.account_id || a.id) : a.id;
    if (id === cfg?.active_account) return;
    setSwitching(id);
    try {
      const { data } = await api.put(`/admin/social/${provider}/account`, { account_id: id, account_name: a.name || "" });
      toast.success(data.message);
      await reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid={`social-card-${provider}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Icon size={32} weight="duotone" className={`${meta.color} shrink-0 mt-0.5`} />
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight">{meta.label}</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              {cfg?.connected
                ? cfg.active_account
                  ? <>Account attivo: <span className="text-white/80">{cfg.active_account_name || cfg.active_account}</span> — campagne degli ultimi 30 giorni.</>
                  : "Collegato: seleziona qui sotto l'account da gestire."
                : "Collega l'account per leggere e gestire le campagne da qui."}
            </p>
          </div>
        </div>
        {cfg?.connected && (
          <div className="flex gap-2">
            <Button data-testid={`social-${provider}-reload`} onClick={loadCampaigns} disabled={loading || !cfg.active_account} variant="ghost"
              className="h-9 px-4 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
              <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Ricarica
            </Button>
            <Link to="/impostazioni" data-testid={`social-${provider}-settings-link`} title="Credenziali e connessione"
              className="h-9 w-9 border border-white/20 text-white/60 hover:text-white hover:bg-white/5 rounded-full inline-flex items-center justify-center transition-colors">
              <GearSix size={15} />
            </Link>
          </div>
        )}
      </div>

      {cfg && !cfg.connected && (
        <div className="mt-5 p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`social-${provider}-not-connected`}>
          <p className="text-sm text-white/60">
            {cfg.has_credentials
              ? `Credenziali salvate: completa il collegamento con "${provider === "meta" ? "Accedi con Facebook" : "Collega TikTok"}" dalle Impostazioni.`
              : "Nessun account collegato. Configura App ID e Secret nelle Impostazioni: trovi anche la guida passo-passo per creare l'app."}
          </p>
          <Link to="/impostazioni" data-testid={`social-${provider}-goto-settings`}
            className={`inline-flex items-center gap-1 mt-3 text-xs font-semibold ${provider === "meta" ? "text-sky-300 hover:text-sky-200" : "text-fuchsia-300 hover:text-fuchsia-200"} transition-colors`}>
            Vai a Impostazioni →
          </Link>
        </div>
      )}

      {cfg?.connected && (
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid={`social-${provider}-account-selector`}>
          <span className="text-[10px] tracking-[0.2em] uppercase text-white/35 mr-1">Account</span>
          {accounts === null && <span className="text-xs text-white/40">Caricamento account...</span>}
          {accounts && accounts.length === 0 && <span className="text-xs text-amber-400">Nessun account pubblicitario accessibile.</span>}
          {(accounts || []).map((a) => {
            const id = provider === "meta" ? (a.account_id || a.id) : a.id;
            const active = cfg.active_account === id;
            return (
              <button key={id} type="button" disabled={switching === id} onClick={() => selectAccount(a)}
                data-testid={`social-${provider}-account-${id}`}
                className={`h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors ${active ? (provider === "meta" ? "bg-sky-500/15 text-sky-300 border-sky-400/50" : "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/50") : "border-white/15 text-white/55 hover:text-white hover:bg-white/5"}`}>
                {switching === id ? "..." : (a.name || id)}
              </button>
            );
          })}
        </div>
      )}

      {cfg?.connected && cfg.active_account && (
        <>
          {error && <div className="mt-4 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-xs" data-testid={`social-${provider}-error`}>{error}</div>}
          {loading && !campaigns && <p className="text-xs text-white/40 py-4 text-center">Caricamento campagne...</p>}
          {campaigns && campaigns.length === 0 && <p className="text-xs text-white/40 py-4 text-center" data-testid={`social-${provider}-empty`}>Nessuna campagna nell'account.</p>}
          {campaigns && campaigns.length > 0 && <CampaignsTable provider={provider} campaigns={campaigns} />}
        </>
      )}
    </div>
  );
};

export default function MarketingSocialPage() {
  const [cfg, setCfg] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/admin/social/config").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const ok = params.get("social_connected");
    if (ok) {
      toast.success(`Account ${ok === "meta" ? "Meta" : "TikTok"} collegato! Seleziona ora l'account da gestire.`);
      setParams({}, { replace: true });
    }
    const err = params.get("social_error");
    if (err) {
      toast.error(`Connessione non riuscita: ${err}`);
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-social-page">
      <div className="mb-10 animate-fade-up flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/marketing" data-testid="back-to-marketing" className="inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors mb-4">
            <CaretLeft size={13} /> Marketing
          </Link>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
            <ShareNetwork size={13} className="text-fuchsia-400" /> Canale Social
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Social</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Campagne pubblicitarie su Facebook, Instagram e TikTok. Collega gli account dalle Impostazioni, poi gestisci tutto da qui.
          </p>
        </div>
        <Link to="/marketing/social/calendario" data-testid="goto-calendar-button"
          className="h-11 px-6 inline-flex items-center gap-2 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm transition-colors">
          <CalendarBlank size={15} /> Calendario editoriale
        </Link>
      </div>
      <div className="space-y-6">
        <ProviderCard provider="meta" cfg={cfg?.meta} reload={load} />
        <ProviderCard provider="tiktok" cfg={cfg?.tiktok} reload={load} />
      </div>
    </main>
  );
}
